import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';

import loomIcon from './img/loom.svg';
import googleDriveIcon from './img/google-drive.svg';

const ICON_SIZE: Record<'small' | 'medium' | 'large', number> = {
	small: 16,
	medium: 20,
	large: 28,
};

const i18n = defineMessages({
	loomIconAlt: {
		id: 'space-curation.stacked-content-type-icons.loom-icon-alt',
		defaultMessage: 'Loom icon',
		description: 'Alt text for the Loom logo in the stacked content type icons',
	},
	googleDriveIconAlt: {
		id: 'space-curation.stacked-content-type-icons.google-drive-icon-alt',
		defaultMessage: 'Google Drive icon',
		description: 'Alt text for the Google Drive logo in the stacked content type icons',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	iconSlotFirst: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
		zIndex: 100,
		transform: 'rotate(-8deg)',
	},
	iconSlotSecond: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.050'),
		position: 'relative',
		zIndex: 1,
		transform: 'rotate(8deg)',
	},
});

type StackedContentTypeIconsProps = {
	variant?: 'small' | 'medium' | 'large';
	testId?: string;
};

export const StackedContentTypeIcons = ({
	variant = 'small',
	testId = 'stacked-content-type-icons',
}: StackedContentTypeIconsProps) => {
	const { formatMessage } = useIntl();
	const size = ICON_SIZE[variant];
	return (
		<Box xcss={styles.wrapper} testId={testId}>
			<Box xcss={styles.iconSlotFirst} aria-hidden="true">
				<img src={loomIcon} width={size} height={size} alt={formatMessage(i18n.loomIconAlt)} />
			</Box>
			<Box xcss={styles.iconSlotSecond} aria-hidden="true">
				<img
					src={googleDriveIcon}
					width={size}
					height={size}
					alt={formatMessage(i18n.googleDriveIconAlt)}
				/>
			</Box>
		</Box>
	);
};
